import { Box, Button, Typography } from "@mui/material";
import { useCallback } from "react";

import { Dialog } from "../../atoms/Dialog/Dialog";

// Translatable strings
const tCancel = "Cancel";
const tDelete = "Delete";
const tCannotUndo = "This action cannot be undone.";
const tConfirmDelete = "Are you sure you want to delete this";

interface ConfirmDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemType: string;
  itemName: string | string[];
  itemDescription?: string;
}

export const ConfirmDeleteDialog = ({
  open,
  onClose,
  onConfirm,
  itemType,
  itemName,
  itemDescription,
}: ConfirmDeleteDialogProps) => {
  const handleConfirm = useCallback(() => {
    onConfirm();
    onClose();
  }, [onConfirm, onClose]);

  return (
    <Dialog
      actions={
        <Box display="grid" gap="2rem" gridAutoColumns="minmax(0, 1fr)" gridAutoFlow="row" padding="1rem">
          <Typography variant="body1" gutterBottom data-testid="confirm-delete-message">
            {`${tConfirmDelete} ${itemType}?`}
          </Typography>

          <Box sx={{ bgcolor: "grey.50", borderRadius: 1, p: 2 }} data-testid="item-details">
            {Array.isArray(itemName) ? (
              itemName.map((name, index) => (
                <Typography
                  key={index}
                  variant="subtitle2"
                  fontWeight="bold"
                  gutterBottom
                  data-testid={`item-name-${index}`}
                >
                  {name}
                </Typography>
              ))
            ) : (
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom data-testid="item-name">
                {itemName}
              </Typography>
            )}
            {itemDescription && (
              <Typography variant="body2" color="text.secondary" data-testid="item-description">
                {itemDescription}
              </Typography>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" data-testid="warning-message">
            {tCannotUndo}
          </Typography>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
            <Button onClick={onClose} variant="outlined" data-testid="cancel-button">
              {tCancel}
            </Button>
            <Button onClick={handleConfirm} variant="contained" color="error" data-testid="delete-button">
              {`${tDelete} ${itemType}`}
            </Button>
          </Box>
        </Box>
      }
      dialogType="custom"
      maxWidth="sm"
      onDialogClose={onClose}
      open={open}
      title={`${tDelete} ${itemType}`}
    />
  );
};
